import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { TopicHeatFlames, TopicHeatScore, TopicHeatTrend } from './TopicHeat';
import { currentTopicSettings, topicSettings } from '../state/topicSettings';
import { hotTopics, orderedHotTopics } from '../state/hotTopics';
/**
 * The hot topics.
 *
 * Three places to show them (the card grid on the index page, the list in
 * the sidebar, the bar on mobile), each with its own switch, and one source
 * of data: the topics that are hottest right now.
 *
 * The order is the heat ranking until an admin curates the list (round 4
 * writes the curated order into the settings; until then `hotCurated` is
 * empty and the ranking stands in).
 */
export default class HotTopics extends Component {
  topics = [];
  loading = true;
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }

  /**
   * Fetch the hot topics.
   *
   * The topics themselves are shared with the rest of the page (the column
   * beside the posts shows the same list), so this does not fire a request
   * per component.
   *
   * The settings come first: they decide whether this place shows anything
   * at all, and how many topics it wants.
   */
  async load() {
    const settings = await topicSettings();
    const limit = this.attrs.limit ?? settings.hotLimit;
    try {
      const topics = await hotTopics();
      this.topics = orderedHotTopics(topics, settings.hotCurated, limit);
    } catch {
      this.topics = [];
    }
    this.loading = false;
    m.redraw();
  }
  view(vnode) {
    const variant = this.attrs.variant ?? 'cards';
    const settings = currentTopicSettings();

    // Each place has its own switch.
    if (variant === 'cards' && !settings.hotShowHome) return null;
    if (variant === 'list' && !settings.hotShowSidebar) return null;
    if (variant === 'bar' && !settings.hotShowMobileBar) return null;
    if (this.loading) return variant === 'bar' ? null : m(LoadingIndicator, null);
    if (!this.topics.length) {
      return variant === 'cards' ? m("p", {
        className: "HotTopics-empty"
      }, app.translator.trans('ziiven-topics.forum.hot.empty')) : null;
    }
    if (variant === 'list') return m("div", {
      className: "HotTopics HotTopics--list"
    }, this.rows());
    if (variant === 'bar') return m("div", {
      className: "HotTopics HotTopics--bar"
    }, this.rows());
    return m("div", {
      id: "hot-topics",
      className: 'HotTopics HotTopics--' + variant + (this.attrs.className ? ' ' + this.attrs.className : '')
    }, m("div", {
      className: "HotTopics-head"
    }, m("h2", {
      className: "HotTopics-title"
    }, m(Icon, {
      name: "fas fa-fire"
    }), " ", app.translator.trans('ziiven-topics.forum.hot.title')), m("span", {
      className: "HotTopics-lede"
    }, app.translator.trans('ziiven-topics.forum.hot.lede'))), m("div", {
      className: "HotTopics-cards"
    }, this.cards()));
  }

  /**
   * The compact list: name and level, as the sidebar shows it.
   */
  rows() {
    return this.topics.map(topic => m("div", {
      className: "HotTopics-row",
      key: topic.id()
    }, m(Link, {
      className: "taglink HotTopics-name",
      href: app.route('topic', {
        name: topic.nameKey()
      })
    }, "#", topic.name()), m("span", {
      className: "HotTopics-rowLevel"
    }, m(TopicHeatFlames, {
      level: topic.heatLevel()
    }))));
  }

  /**
   * The full card: rank, name, level, score, trend and how many discussions
   * use the topic.
   */
  cards() {
    return this.topics.map((topic, index) => m("div", {
      className: "HotTopics-card",
      key: topic.id()
    }, m("div", {
      className: "HotTopics-cardTop"
    }, m("span", {
      className: 'HotTopics-rank' + (index < 3 ? ' HotTopics-rank--' + (index + 1) : '')
    }, index + 1), m(Link, {
      className: "taglink HotTopics-name",
      href: app.route('topic', {
        name: topic.nameKey()
      })
    }, "#", topic.name()), m("span", {
      className: "HotTopics-spacer"
    }), m(TopicHeatFlames, {
      level: topic.heatLevel()
    })), m("div", {
      className: "HotTopics-cardFoot"
    }, m(TopicHeatScore, {
      score: topic.heatScore(),
      high: topic.heatLevel() >= 5
    }), m(TopicHeatTrend, {
      trend: topic.heatTrend()
    }), m("span", {
      className: "HotTopics-spacer"
    }), m("span", {
      className: "HotTopics-discussions"
    }, app.translator.trans('ziiven-topics.forum.hot.discussions', {
      count: topic.discussionCount()
    })))));
  }
}
flarum.reg.add('ziiven-topics', 'forum/components/HotTopics', HotTopics);